import React, { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import Modal from "../../../../../layout/modal";
import { BASE_ITEMS, PROFILE_KEY, defaultSelection, descendantKeys, flatten, visibleItems } from "../extract/tree";

const PROFILE_ACTIONS = [
  { value: "keep", label: "Keep" },
  { value: "archive", label: "Archive" },
  { value: "delete", label: "Delete" }
];

function DeletePersonModal({ show, person, onOk, onCancel, onExtract }) {
  const items = useMemo(() => visibleItems(BASE_ITEMS, person?.flags || 0), [person]);
  const selectableKeys = useMemo(() => flatten(items).filter((item) => item.key !== PROFILE_KEY).map((item) => item.key), [items]);
  const [selected, setSelected] = useState({});
  const [profileAction, setProfileAction] = useState("archive");
  const profileDelete = profileAction === "delete";

  useEffect(() => {
    if (!show) return;
    setSelected(defaultSelection(items));
    setProfileAction(person?.archive > 0 ? "keep" : "archive");
  }, [show, items, person]);

  const isChecked = (key) => profileDelete || Boolean(selected[key]);

  const onToggle = (item) => {
    if (profileDelete || item.key === PROFILE_KEY) return;
    const checked = !isChecked(item.key);
    const keys = [item.key, ...descendantKeys(item)];
    setSelected((prev) => ({
      ...prev,
      ...keys.reduce((memo, key) => {
        memo[key] = checked;
        return memo;
      }, {})
    }));
  };

  const setAll = (checked) => {
    if (profileDelete) return;
    setSelected({
      ...selectableKeys.reduce((memo, key) => {
        memo[key] = checked;
        return memo;
      }, {}),
      [PROFILE_KEY]: true
    });
  };

  const onProfileActionChange = (action) => {
    setProfileAction(action);
    if (action === "delete") {
      setSelected(defaultSelection(items));
    }
  };

  const handleOk = () => {
    onOk?.({
      profileAction,
      selection: {
        ...selected,
        [PROFILE_KEY]: true
      }
    });
  };

  const renderProfile = (item) => (
    <div key={item.key} className="mb-3">
      <div className="fw-bold mb-2">{item.caption}</div>
      <div className="d-flex flex-wrap gap-3 mb-2">
        {PROFILE_ACTIONS.map((action) => (
          <Form.Check
            key={action.value}
            id={`delete-profile-${action.value}`}
            type="radio"
            name="delete-profile-action"
            label={action.label}
            checked={profileAction === action.value}
            onChange={() => onProfileActionChange(action.value)}
          />
        ))}
      </div>
      {item.children?.map((child) => renderItem(child, 1, true))}
    </div>
  );

  const renderItem = (item, depth = 0, parentChecked = true) => {
    if (item.key === PROFILE_KEY) return renderProfile(item);
    const checked = parentChecked && isChecked(item.key);
    return (
      <div key={item.key} className={depth === 0 ? "mb-2" : "mt-1"} style={{ marginLeft: depth * 22 }}>
        <Form.Check
          id={`delete-${item.key}`}
          type="checkbox"
          label={item.caption}
          checked={checked}
          disabled={profileDelete || !parentChecked}
          onChange={() => onToggle(item)}
        />
        {item.children?.map((child) => renderItem(child, depth + 1, checked))}
      </div>
    );
  };

  return (
    <Modal
      show={show}
      title="Delete Person"
      body="Deletion is permanent. You should create a full extract prior to deletion."
      size="lg"
      buttons={[
        {
          caption: "Complete Extract",
          onClick: onExtract,
          variant: "outline-primary",
          enabled: true,
          display: true
        },
        {
          caption: profileAction === "delete" ? "delete" : "apply",
          onClick: handleOk,
          variant: "danger",
          enabled: true,
          display: true
        },
        {
          default: true,
          caption: "cancel",
          onClick: onCancel,
          variant: "primary",
          enabled: true,
          display: true
        }
      ]}
    >
      <div className="d-flex gap-3 mb-3 mt-3">
        <button type="button" className="btn btn-link p-0" disabled={profileDelete} onClick={() => setAll(true)}>
          check all
        </button>
        <button type="button" className="btn btn-link p-0" disabled={profileDelete} onClick={() => setAll(false)}>
          uncheck all
        </button>
      </div>
      <div className="border rounded p-3">
        {items.map((item) => renderItem(item))}
      </div>
    </Modal>
  );
}

export default DeletePersonModal;
